/**
 * Website Studio autosave (W4) — the debounced, coalescing PATCH queue.
 * No save button, no lost work, no API hammering: edits to the same
 * entity within the debounce window collapse to ONE request carrying
 * the latest payload; distinct entities flush independently. Pure
 * enough to unit-test with fake timers.
 */

export interface AutosaveOptions {
  /** Debounce per entity key. ~800ms: fast enough to feel continuous,
   *  slow enough that typing a sentence is one request. */
  debounceMs?: number;
  onError?: (key: string, err: unknown) => void;
  /** Called when the queue transitions between idle and pending/in-flight. */
  onBusyChange?: (busy: boolean) => void;
}

type Sender = (payload: unknown) => Promise<unknown>;

interface PendingEntry {
  payload: unknown;
  send: Sender;
  timer: ReturnType<typeof setTimeout>;
}

export class AutosaveQueue {
  private pending = new Map<string, PendingEntry>();
  private inFlight = new Map<string, Promise<void>>();
  private readonly debounceMs: number;
  private readonly onError: ((key: string, err: unknown) => void) | undefined;
  private readonly onBusyChange: ((busy: boolean) => void) | undefined;
  private sends = 0;

  constructor(opts: AutosaveOptions = {}) {
    this.debounceMs = opts.debounceMs ?? 800;
    this.onError = opts.onError;
    this.onBusyChange = opts.onBusyChange;
  }

  /** Total requests actually sent — the coalescing test reads this. */
  get sendCount(): number {
    return this.sends;
  }

  get busy(): boolean {
    return this.pending.size > 0 || this.inFlight.size > 0;
  }

  /**
   * Queue (or re-queue) a save for `key`. The latest payload wins; the
   * debounce timer restarts on every call.
   */
  save(key: string, payload: unknown, send: Sender): void {
    const existing = this.pending.get(key);
    if (existing) clearTimeout(existing.timer);
    const wasBusy = this.busy;
    const timer = setTimeout(() => void this.flushKey(key), this.debounceMs);
    this.pending.set(key, { payload, send, timer });
    if (!wasBusy && this.onBusyChange) this.onBusyChange(true);
  }

  /** Flush one key now (used on blur / before Publish). */
  async flushKey(key: string): Promise<void> {
    const entry = this.pending.get(key);
    if (!entry) return;
    clearTimeout(entry.timer);
    this.pending.delete(key);

    // If a save for this key is already in flight, wait for it — order
    // is preserved and the latest payload still lands last.
    const prior = this.inFlight.get(key);
    if (prior) await prior.catch(() => {});

    const run = (async () => {
      try {
        this.sends += 1;
        await entry.send(entry.payload);
      } catch (err) {
        this.onError?.(key, err);
      }
    })();
    this.inFlight.set(key, run);
    await run;
    if (this.inFlight.get(key) === run) this.inFlight.delete(key);
    if (!this.busy && this.onBusyChange) this.onBusyChange(false);
  }

  /** Flush everything (Publish presses this first). */
  async flushAll(): Promise<void> {
    const keys = [...this.pending.keys()];
    await Promise.all(keys.map((k) => this.flushKey(k)));
    await Promise.all([...this.inFlight.values()].map((p) => p.catch(() => {})));
  }
}
